import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Pagination,
  TextField,
  InputAdornment,
  Paper,
  Avatar,
  Fade,
  Zoom,
  Divider,
  Snackbar,
  Alert,
  CardMedia,
} from '@mui/material';
import DiamondIcon from '@mui/icons-material/Diamond';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import StarIcon from '@mui/icons-material/Star';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#b8942e',
};

const News = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const newsArticles = [
    {
      id: 1,
      title: "4 Expenses You Should Never Put on Your Business Credit Card",
      author: "Beverly Harzog",
      excerpt: "Although the majority of credit cards in circulation are for consumers, the popularity of business credit cards is on the upswing. According to a 2018 report by the Mercator Advisory Group, business credit card usage has grown significantly. Many business owners are discovering the convenience and rewards that come with using business credit cards for their daily operations.",
      date: "December 15, 2024",
      category: "Business Tips",
      image: "/images/Credit Card.jpg",
    },
    {
      id: 2,
      title: "The Secret to Mastercard's Success",
      author: "Matthew Cochrane",
      excerpt: "For investors, the ideal stock would probably be one that operates in a growing market, has a sustainable competitive advantage, and faces little competition. Enter Mastercard (NYSE:MA). The credit card giant has transformed the way the world makes payments, creating a network effect that becomes more valuable as more participants join.",
      date: "December 10, 2024",
      category: "Industry News",
      image: "/images/Mastercard’s Success.jpg",
    },
    {
      id: 3,
      title: "Could Mastercard Be a Millionaire-Maker Stock?",
      author: "Jason Hall",
      excerpt: "While Mastercard (NYSE:MA) has corporate roots more than a half century old and has existed under the current name since the late 1970s, it only went public about 13 years ago. Since its IPO, the stock has delivered staggering returns that would have turned a modest investment into a small fortune.",
      date: "December 5, 2024",
      category: "Investment",
      image: "/images/Maker Stock.jpg",
    },
  ];

  const categories = ["All", "Business Tips", "Industry News", "Investment"];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredArticles = newsArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const articlesPerPage = 5;
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const displayedArticles = filteredArticles.slice((page - 1) * articlesPerPage, page * articlesPerPage);

  const handleSubscribe = () => {
    if (subscribeEmail) {
      setSnackbar({ open: true, message: 'Successfully subscribed to our newsletter!', severity: 'success' });
      setSubscribeEmail('');
    }
  };

  return (
    <Box>
      {/* Hero Section - Increased Height with Reduced Overlay Opacity */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 12, md: 16 },
                minHeight: { xs: 450, md: 700},
          color: 'white', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          backgroundImage: 'url("/images/herobackground.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13,17,23,0.4) 0%, rgba(26,26,46,0.3) 100%)',
          }
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box>
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card"
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 3,
                  backgroundColor: 'transparent',
                  '& img': { objectFit: 'contain' }
                }}
                variant="square"
              />
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '3.5rem' }, 
                  mb: 2, 
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${colors.premiumGold} 0%, ${colors.lightGold} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: -0.5,
                }}
              >
                DIAMOND CARD NEWS
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ color: colors.premiumGold, fontWeight: 500, letterSpacing: 1 }}
              >
                Keeping you updated
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Search and Filter */}
        <Paper elevation={0} sx={{ p: 4, mb: 5, borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search news articles..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: colors.premiumGold }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover fieldset': { borderColor: colors.premiumGold },
                    '&.Mui-focused fieldset': { borderColor: colors.premiumGold },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                {categories.map((category, idx) => (
                  <Chip
                    key={idx}
                    label={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setPage(1);
                    }}
                    sx={{
                      bgcolor: selectedCategory === category ? colors.premiumGold : 'transparent',
                      color: selectedCategory === category ? colors.navy : colors.textLight,
                      fontWeight: 600,
                      borderColor: colors.premiumGold,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${colors.premiumGold}1A` },
                    }}
                    variant={selectedCategory === category ? "filled" : "outlined"}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* News Articles with Images */}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: colors.navy, mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Latest News & Updates
        </Typography>

        <Grid container spacing={4}>
          {displayedArticles.map((article, idx) => (
            <Zoom in timeout={300 + idx * 150} key={article.id}>
              <Grid item xs={12}>
                <Card sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  borderRadius: 4, 
                  overflow: 'hidden',
                  transition: 'all 0.3s', 
                  '&:hover': { 
                    transform: 'translateY(-4px)', 
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)' 
                  } 
                }}>
                  {/* Article Image */}
                  <CardMedia
                    component="img"
                    sx={{ 
                      width: { xs: '100%', md: 300 },
                      height: { xs: 200, md: 'auto' },
                      objectFit: 'cover',
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      }
                    }}
                    image={article.image}
                    alt={article.title}
                  />
                  
                  {/* Article Content */}
                  <CardContent sx={{ p: 4, flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip 
                        label={article.category} 
                        size="small" 
                        sx={{ bgcolor: colors.premiumGold, color: colors.navy, fontWeight: 600 }} 
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 14, color: colors.premiumGold }} />
                        <Typography variant="caption" color="text.secondary">{article.date}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, color: colors.premiumGold }} />
                        <Typography variant="caption" color="text.secondary">by {article.author}</Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: colors.navy, mb: 2, fontSize: '1.25rem' }}>
                      {article.title}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7, mb: 3 }}>
                      {article.excerpt}
                    </Typography>
                    
                    <Button 
                      variant="text" 
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        color: colors.premiumGold, 
                        textTransform: 'none', 
                        fontWeight: 600,
                        '&:hover': { transform: 'translateX(5px)', transition: 'transform 0.2s' }
                      }}
                    >
                      Read Full Article
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Zoom>
          ))}
        </Grid>

        {/* No Results */}
        {filteredArticles.length === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No articles found matching your search.
            </Typography>
            <Button 
              variant="outlined" 
              sx={{ mt: 2, borderColor: colors.premiumGold, color: colors.navy }}
              onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
            >
              Clear Filters
            </Button>
          </Paper>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              sx={{
                '& .MuiPaginationItem-root': { fontWeight: 600 },
                '& .Mui-selected': { bgcolor: colors.premiumGold, color: colors.navy },
              }}
              size="large"
            />
          </Box>
        )}

        {/* Newsletter Signup */}
        <Paper 
          elevation={0}
          sx={{ 
            mt: 8, 
            p: { xs: 4, md: 5 }, 
            textAlign: 'center', 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.charcoal} 100%)`,
            border: `1px solid ${colors.premiumGold}33`,
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: colors.premiumGold,
              boxShadow: `0 10px 30px ${colors.premiumGold}20`,
            }
          }}
        >
          <DiamondIcon sx={{ fontSize: 50, color: colors.premiumGold, mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ color: colors.premiumGold, fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Subscribe to Our Newsletter
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, maxWidth: 500, mx: 'auto', color: '#ccc' }}>
            Get the latest news, updates, and financial tips delivered directly to your inbox.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, maxWidth: 500, mx: 'auto', flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              placeholder="Your Email Address"
              variant="outlined"
              fullWidth
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              sx={{ 
                bgcolor: 'white', 
                borderRadius: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 2 }
              }}
            />
            <Button 
              variant="contained" 
              onClick={handleSubscribe}
              endIcon={<SendIcon />}
              sx={{ 
                bgcolor: colors.premiumGold,
                color: colors.navy,
                fontWeight: 700, 
                whiteSpace: 'nowrap',
                px: 4,
                '&:hover': { bgcolor: colors.lightGold, transform: 'translateY(-2px)' },
                transition: 'all 0.3s ease',
              }}
            >
              Subscribe
            </Button>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ bgcolor: colors.navy, color: colors.premiumGold, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default News;